import { ButtonHTMLAttributes, Ref, useEffect } from 'react';
import {
  VisuallyHidden,
  Button as ReakitButton,
  useButton as useReakitButton,
  ButtonOptions as ReakitButtonOptions,
  ButtonHTMLProps as ReakitButtonHTMLProps,
} from 'reakit';
import { omit, pick } from '@styled-system/props';
import { createHook } from 'reakit-system';
import type { BoxOptions } from 'reakit/ts';
import warning from 'tiny-warning';
import { theme } from '@mos-cat/ds-theme';
import { WHCM } from '../constants';

import { ecss, cx } from '../reexports/emotion';
import { useBox } from '../Box/Box';
import {
  useFocusable,
  StylesOptions,
  useStyles,
  useTheme,
  useRequiredPropWarning,
} from '../hooks';
import { useText } from '../Text/Text';
import LoadingIndicator from './LoadingIndicator';
import { ComponentWithAs, forwardRefWithAs, PropsWithAs } from '../as';

import {
  press,
  kind as getKind,
  vertSize as getVertSize,
  Kind,
  VertSize,
} from './variants';
import { mergeRefs } from '../utils/mergeRefs';

export type ButtonHTMLProps = ButtonHTMLAttributes<HTMLButtonElement> &
  ReakitButtonHTMLProps;
export type ButtonOptions = BoxOptions &
  ReakitButtonOptions & {
    kind: Kind;
    vertSize?: VertSize;
    pressed?: boolean;
  } & StylesOptions &
  (
    | {
        /**
         * Включает визуальную и аудио индикацию производимого действия.
         * Важно что состояние `loading` не предотвращает повторные нажатия на кнопку.
         * Не рекомендуется использовать на безрамочных кнопках, поскольку спиннер показывается _вместо_ текста по центру кнопки, не изменяя её размера.
         */
        loading: true;
        /**
         * Сообщение для незрячих пользователей которое будет прочитано когда кнопка перейдёт в состояние `loading`
         */
        loadingMessage: string;
      }
    | {
        loading?: false;
        loadingMessage?: string;
      }
  );

export type ButtonProps = ButtonOptions & ButtonHTMLProps;

const useLoadingWarning = (options: ButtonOptions) => {
  useEffect(() => {
    warning(
      !options.loading || options.loadingMessage,
      `Свойство loadingMessage обязательно, если Button в состоянии loading.`
    );
  }, [options.loading, options.loadingMessage]);
};

function getFilledArray(
  options: string | number | (string | number)[],
  breakpoints: (string | number)[]
): (string | number | null)[] {
  return Array.isArray(options)
    ? Array<string | number | null>(breakpoints.length)
        .fill(null)
        .map((_, index) => {
          const currentOption = options[index];
          if (currentOption) return currentOption;
          const filledPreviousOptions = options.slice(0, index).filter(Boolean);
          return filledPreviousOptions[filledPreviousOptions.length - 1];
        })
    : Array<string | number | null>(breakpoints.length).fill(options);
}

export const useButton = createHook<ButtonOptions, ButtonHTMLProps>({
  name: 'Button',
  keys: ['vertSize', 'kind', 'loading', 'loadingMessage', 'pressed'],
  compose: [
    useText,
    useBox,
    useTheme,
    useStyles,
    useFocusable,
    useReakitButton,
  ],
  useOptions(options) {
    const { kind, vertSize } = options;
    useRequiredPropWarning(Boolean(options.kind), 'Button', 'kind');
    useLoadingWarning(options);
    const { breakpoints } = theme;
    const fullBreakpoints = [0, ...breakpoints];
    const kindArray = getFilledArray(kind, fullBreakpoints);
    const vertSizeArray = getFilledArray(vertSize ?? '48', fullBreakpoints);
    const loadingStyle = options.loading
      ? {
          '& span.Button-children': {
            color: 'transparent',
            [WHCM]: { color: 'Window' },
          },
        }
      : undefined;

    return {
      ...options,
      borderRadius: 'button',
      border: 1,
      position: 'relative',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      styles: {
        ...options.styles,
        ...loadingStyle,
        ...fullBreakpoints.reduce(
          (result, breakpoint, index) => ({
            ...result,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- в либе styled-system variants возвращает any
            [`@media (min-width: ${breakpoint})`]: {
              // Порядок имеет значение! Для borderless кнопки getKind сбрасывает боковые паддинги,
              // установленные с помощью getVertSize.
              ...getVertSize({ vertSize: vertSizeArray[index] }),
              ...getKind({ kind: kindArray[index] }),
              ...press(kind)(options.pressed),
            },
          }),
          {}
        ),

        [WHCM]: {
          '&[aria-disabled="true"]': {
            borderColor: 'GrayText',
            color: 'GrayText',
          },
        },
      },
    };
  },
  useProps(_, props) {
    return {
      ...props,
      focusable: true,
      className: cx(
        props.className,
        ecss({
          label: 'Button',
        })
      ),
    };
  },
});

function ButtonComponent(
  {
    as: Type = ReakitButton,
    kind,
    vertSize = 48,
    loading,
    loadingMessage,
    pressed,
    styles,
    ...props
  }: PropsWithAs<ButtonProps>,
  ref: Ref<HTMLButtonElement>
) {
  const buttonProps = useButton(
    {
      vertSize,
      loading,
      loadingMessage,
      pressed,
      kind,
      styles,
      ...pick(props),
    } as ButtonOptions,
    omit(props) as ButtonHTMLProps
  );
  const refs = buttonProps.ref ? [ref, buttonProps.ref] : [ref];
  return (
    <Type {...buttonProps} ref={mergeRefs<HTMLButtonElement>(refs)}>
      <span className="Button-children">{props.children}</span>
      {loading && <LoadingIndicator aria-hidden="true" />}
      <VisuallyHidden role="status" aria-live="polite">
        {loading && loadingMessage}
      </VisuallyHidden>
    </Type>
  );
}

export const Button: ComponentWithAs<ButtonProps, 'button'> = forwardRefWithAs<
  ButtonProps,
  'button'
>(ButtonComponent);
